import {
  LayoutDashboard,
  BarChart3,
  BookOpen,
  Calendar,
  Clock,
  Users,
  Target,
  Mail,
  FileText,
  Settings,
  GraduationCap,
  Inbox,
  UserPlus,
  Building2,
  ListChecks,
  PencilLine,
  Gamepad2,
  Megaphone,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

export type AppRole = "student" | "instructor" | "parent" | "admin";

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Match exact path only (no descendants). */
  exact?: boolean;
  /**
   * If true, guests clicking this item see a sign-up prompt instead of
   * navigating. Practice/learn items leave this off so guests can use them.
   */
  requiresAuth?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Single source of truth for sidebar nav per role.
 * Drives both <AppSidebar /> and breadcrumbs.
 */
export const NAV_BY_ROLE: Record<AppRole, NavGroup[]> = {
  student: [
    {
      label: "Learn",
      items: [
        { label: "Dashboard", to: "/student/dashboard", icon: LayoutDashboard, exact: true, requiresAuth: true },
        { label: "Practice tests", to: "/grade", icon: BookOpen },
        { label: "AI practice test", to: "/practice/ai", icon: Sparkles },
        { label: "Games", to: "/games", icon: Gamepad2 },
        { label: "Worksheets", to: "/worksheets", icon: FileText },
        { label: "Writing pad", to: "/writing", icon: PencilLine },
      ],
    },
    {
      label: "Progress",
      items: [
        { label: "Analysis", to: "/analysis", icon: BarChart3, requiresAuth: true },
      ],
    },
    {
      label: "Schedule",
      items: [
        { label: "Calendar", to: "/student/calendar", icon: Calendar, requiresAuth: true },
        { label: "Tutoring", to: "/student/tutoring", icon: Calendar, requiresAuth: true },
      ],
    },
    {
      label: "Connect",
      items: [
        { label: "Parent portal", to: "/parent/auth", icon: Users, requiresAuth: true },
        { label: "Invitations", to: "/student/invitations", icon: Mail, requiresAuth: true },
      ],
    },
  ],
  instructor: [
    {
      label: "Overview",
      items: [
        { label: "Dashboard", to: "/instructor/dashboard", icon: LayoutDashboard, exact: true },
        { label: "Sessions", to: "/instructor/sessions", icon: Clock },
        { label: "Calendar", to: "/instructor/calendar", icon: Calendar },
        { label: "Analytics", to: "/instructor/analytics", icon: BarChart3 },
      ],
    },
    {
      label: "Teaching",
      items: [
        { label: "Tests", to: "/instructor/tests", icon: Target },
        { label: "Student lists", to: "/instructor/students", icon: Users },
        { label: "Tutoring schedules", to: "/instructor/tutoring", icon: Calendar },
        { label: "Courses", to: "/org/courses", icon: BookOpen },
      ],
    },
    {
      label: "Resources",
      items: [
        { label: "Worksheets", to: "/worksheets", icon: FileText },
        { label: "Games", to: "/games", icon: Gamepad2 },
        { label: "Writing pad", to: "/writing", icon: PencilLine },
      ],
    },
    {
      label: "Workspace",
      items: [
        { label: "Organizations", to: "/orgs", icon: Building2 },
      ],
    },
  ],
  parent: [
    {
      label: "Overview",
      items: [
        { label: "Dashboard", to: "/parent/dashboard", icon: LayoutDashboard, exact: true },
      ],
    },
    {
      label: "My family",
      items: [
        { label: "Children", to: "/parent/children", icon: Users },
        { label: "Calendar", to: "/parent/calendar", icon: Calendar },
        { label: "Tests", to: "/parent/tests", icon: Target },
        { label: "Analysis", to: "/parent/analysis", icon: BarChart3 },
      ],
    },
  ],
  admin: [
    {
      label: "Overview",
      items: [
        { label: "Dashboard", to: "/admin/dashboard", icon: LayoutDashboard, exact: true },
      ],
    },
    {
      label: "Content",
      items: [
        { label: "Questions", to: "/admin/questions", icon: ListChecks },
        { label: "Generator", to: "/admin/generator", icon: GraduationCap },
        { label: "Subjects", to: "/admin/subjects", icon: BookOpen },
        { label: "Reports", to: "/admin/reports", icon: Inbox },
        { label: "Category suggestions", to: "/admin/category-suggestions", icon: UserPlus },
        { label: "AI logs", to: "/admin/logs", icon: BarChart3 },
      ],
    },
    {
      label: "Communications",
      items: [
        { label: "Email templates", to: "/admin/email-templates", icon: Mail },
        { label: "Email campaigns", to: "/admin/email-campaigns", icon: Mail },
        { label: "Broadcasts", to: "/admin/notifications", icon: Megaphone },
      ],
    },
  ],
};

export const SETTINGS_ITEM: NavItem = {
  label: "Settings",
  to: "/settings",
  icon: Settings,
};
